import type { ToolConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/tool-config'
import type { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config'
import type {
  Dictionary,
  Filter,
  RegularFilter,
  RegularFilterConfig,
  RegularFilterSelectedValues,
} from '../models'
// @ts-strict-ignore
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'
import Vue from 'vue'

import { mapActions, mapGetters } from 'vuex'

import { useFilters } from '../composables/use-filters'

import { FilterTypeEnum } from '../enums'
import { ResolveConflictOption } from '../models'

import { ROUTER_CONSTS } from '../router/router.consts'

import { APP_CONSTS } from '../store/app/app.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { CRUD_CONSTS } from '../store/crud/crud.consts'

import { FeaturesTreeUtils } from '../utils/features-tree.utils'
import { FiltersUtils } from '../utils/filters.utils'

export const CrudFormMixin = {
  setup() {
    const { availableFilters } = useFilters()
    return {
      availableFilters,
    }
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      instanceConfig: CONFIG_CONSTS.getters.currentInstance,
    }),
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    }),
    ...mapGetters(CRUD_CONSTS.moduleName, {
      state: CRUD_CONSTS.getters.state,
    }),
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    toolConfig(): ToolConfig {
      const toolsConfig = this.instanceConfig.domains[this.context.domain]?.tools
      const toolConfig = Object.assign(
        {},
        !!toolsConfig && toolsConfig.base,
        !!toolsConfig && toolsConfig[this.dataType],
      )
      return {
        ...toolConfig,
        pivotIds: toolConfig.pivotIds || [],
      }
    },
    readOnly(): boolean {
      const featureNode = FeaturesTreeUtils.getFeatureNode(
        this.dataType,
        this.featuresTree[this.context.domain || DomainEnum.GLOBAL],
      )
      return !featureNode || featureNode.permissionLevel !== PermissionLevelEnum.WRITE
    },
    pivots(): Dictionary<RegularFilter> {
      return Object.values(this.availableFilters as Record<string, Filter>).reduce(
        (pivots: Dictionary<RegularFilter>, filter: Filter) => {
          if (this.toolConfig.pivotIds.includes(filter.config.id)) {
            const filterConfig = filter.config as RegularFilterConfig
            pivots[filter.config.id] = {
              config: {
                ...filterConfig,
                mandatory: true,
              },
              selectedValues: this.getFilterSelectedValues(filterConfig),
            }
          }
          return pivots
        },
        {},
      )
    },
    visiblePivots(): RegularFilter[] {
      return Object.values(this.pivots).filter(
        (pivot: RegularFilter) => pivot.config.values.length > 1,
      ) as RegularFilter[]
    },
    filters(): Dictionary<RegularFilter> {
      return Object.values(this.availableFilters as Record<string, Filter>).reduce(
        (filters: Dictionary<RegularFilter>, filter: Filter) => {
          if (
            filter.config.type === FilterTypeEnum.REGULAR &&
            !this.toolConfig.pivotIds.includes(filter.config.id)
          ) {
            const filterConfig = filter.config as RegularFilterConfig
            filters[filter.config.id] = {
              config: filterConfig,
              selectedValues: this.getFilterSelectedValues(filterConfig),
            }
          }
          return filters
        },
        {},
      )
    },
    valid(): boolean {
      return !Object.values(this.pivots)
        .concat(Object.values(this.filters))
        .some((filter: RegularFilter) => filter.config.mandatory && !filter.selectedValues.length)
    },
  },
  data() {
    return {
      dataType: undefined,
      item: {},
      updateMode: false,
      serviceErrorsFields: [],
      files: undefined,
      conflictingElements: [],
      messages: {
        create: {
          success: undefined,
        },
        update: {
          success: undefined,
        },
      },
    }
  },
  props: {
    loading: Object,
  },
  methods: {
    ...mapActions(CRUD_CONSTS.moduleName, {
      read: CRUD_CONSTS.actions.read,
      create: CRUD_CONSTS.actions.create,
      update: CRUD_CONSTS.actions.update,
    }),
    onItemLoad(): void {
      Object.values(this.pivots)
        .concat(Object.values(this.filters))
        .forEach((filter: RegularFilter) => {
          const defaultSelectedValues = FiltersUtils.getDefaultSelectedValues(filter)

          if (defaultSelectedValues.length) {
            this.onFilterChange(filter, defaultSelectedValues)
          }
        })
    },
    getFilterSelectedValues(filterConfig: RegularFilterConfig): RegularFilterSelectedValues {
      const filterId = filterConfig.toolingId ?? filterConfig.id
      let itemFilterValues: string[] | string
      if (this.toolConfig.pivotIds.includes(filterConfig.id)) {
        itemFilterValues = this.item.context.pivots[filterId]
      } else if (this.item.filters) {
        itemFilterValues = this.item.filters[filterId]
      }
      if (!Array.isArray(itemFilterValues)) {
        itemFilterValues = itemFilterValues ? [itemFilterValues] : []
      }
      return itemFilterValues.map((itemFilterValue: string) => {
        const itemFilterValueConfig = filterConfig.values.find(
          (valueConfig: RegularFilterValueConfig) =>
            (valueConfig.taggingId ?? valueConfig.id) === itemFilterValue,
        )
        return (!!itemFilterValueConfig && itemFilterValueConfig.id) || itemFilterValue
      })
    },
    onFilterChange(filter: RegularFilter, selectedValues: RegularFilterSelectedValues): void {
      let itemFilterValues: string[] | string = selectedValues.map((selectedValue: string) => {
        const selectedValueConfig = filter.config.values.find(
          (valueConfig: RegularFilterValueConfig) => valueConfig.id === selectedValue,
        )
        return selectedValueConfig.taggingId ?? selectedValueConfig.id
      })
      if (filter.config.multiselectDisabled) {
        itemFilterValues = itemFilterValues[0]
      }
      const filterId = filter.config.toolingId ?? filter.config.id
      let itemFiltersObject: Record<string, string | Array<string>>
      if (this.toolConfig.pivotIds.includes(filter.config.id)) {
        itemFiltersObject = this.item.context.pivots
      } else {
        itemFiltersObject = this.item.filters
      }
      Vue.set(itemFiltersObject, filterId, itemFilterValues)
    },
    async onAccept(updatePrevious: Array<any> = []): Promise<void> {
      this.$emit('updateLoading', { visible: true })
      try {
        if (updatePrevious.length) {
          await this.update({
            dataType: this.dataType,
            items: updatePrevious,
            files: this.files,
          })
        }
        if (!this.updateMode) {
          await this.create({
            dataType: this.dataType,
            items: [this.item],
            files: this.files,
          })
          this.$emit('showSnackbar', this.messages.create.success)
        } else {
          await this.update({
            dataType: this.dataType,
            items: [this.item],
            files: this.files,
          })
          this.$emit('showSnackbar', this.messages.update.success)
        }
        this.$emit('updateLoading', { visible: false })
        this.$router.push({
          ...this.$route,
          name: this.$route.meta.parentRouteName,
        })
      } catch (error) {
        if (!!error.details && error.status === 400) {
          this.serviceErrorsFields = error.details.fields
          this.conflictingElements = this.serviceErrorsFields.filter(
            errorField => !!errorField.conflictingElements?.length,
          )[0]?.conflictingElements
        } else {
          this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
        }
      } finally {
        this.$emit('updateLoading', { visible: false })
      }
    },
    onCancel(): void {
      this.$router.push({
        ...this.$route,
        name: this.$route.meta.parentRouteName,
      })
    },
    async resolveConflict(resolveConflictOption: ResolveConflictOption): Promise<void> {
      if (resolveConflictOption === ResolveConflictOption.DISABLE_CURRENT) {
        this.item.enabled = false
        this.onAccept()
      } else {
        await this.update({
          dataType: this.dataType,
          items: this.conflictingElements.map(item => ({
            ...item,
            enabled: false,
          })),
        })
        this.onAccept()
      }
    },
  },
  async created(): Promise<void> {
    const id = this.$route.params[ROUTER_CONSTS.params.id]
    if (id) {
      let item = this.state.data.find((dataItem: any) => dataItem.id === id)
      if (!item || this.state.dataType !== this.dataType) {
        this.$emit('updateLoading', { visible: true })
        try {
          await this.read({ dataType: this.dataType, ids: [id] })
          item = this.state.data.find((dataItem: any) => dataItem.id === id)
          this.$emit('updateLoading', { visible: false })
        } catch (error) {
          if (error.status === 404) {
            this.$router.replace({
              ...this.$route,
              name: this.$route.meta.parentRouteName,
            })
          } else {
            this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
            this.$emit('updateLoading', { visible: false })
          }
        }
      }
      if (item) {
        this.item = Object.assign({}, item)
      }
    }
    this.onItemLoad()
  },
}
